/**
 * Species graph view.
 *
 * Each server-provided candidate is a node sized + faded by its probability.
 * Layout is bounded preset ranked by probability — the most likely species sits
 * at dead center with lower-probability species ringing outward. Labels are
 * gated by candidate count to keep dense views readable.
 */
import type { ConverseCandidate } from '../../lib/api/converse.js';
import { colors } from '../../theme/tokens.js';

export interface SpeciesElement {
  data: {
    id: string;
    label: string;
    displayLabel: string;
    prob: number;
    sizeScore: number;
    family: string;
    rank: number;
  };
  position: { x: number; y: number };
}

function radialPosition(rank: number): { x: number; y: number } {
  if (rank === 0) return { x: 500, y: 360 };
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const radius = 50 * Math.sqrt(rank);
  const angle = rank * goldenAngle;
  return {
    x: 500 + Math.cos(angle) * radius,
    y: 360 + Math.sin(angle) * radius,
  };
}

function speciesLabelLimit(candidateCount: number): number {
  if (candidateCount <= 12) return candidateCount;
  if (candidateCount <= 24) return 8;
  return 6;
}

export function buildSpeciesElements(args: {
  candidates: ConverseCandidate[];
}): { elements: SpeciesElement[]; layout: { name: string; [k: string]: unknown } } {
  const candidates = [...args.candidates]
    .filter((c) => c.probability > 0.001)
    .sort((a, b) => b.probability - a.probability)
    .slice(0, 50);

  const labelLimit = speciesLabelLimit(candidates.length);
  const elements: SpeciesElement[] = candidates.map((c, i) => {
    const label = c.commonNames?.[0] ?? c.scientificName ?? c.speciesId;
    return {
      data: {
        id: c.speciesId,
        label,
        displayLabel: i < labelLimit ? label : '',
        prob: c.probability,
        sizeScore: Math.sqrt(Math.max(c.probability, 0)) * 100,
        family: c.family ?? 'Unknown',
        rank: i,
      },
      position: radialPosition(i),
    };
  });

  return {
    elements,
    layout: {
      name: 'preset',
      fit: true,
      padding: 60,
      animate: true,
      animationDuration: 400,
    },
  };
}

export const speciesStylesheet = [
  {
    selector: 'node',
    style: {
      'width': 'mapData(sizeScore, 0, 100, 28, 88)',
      'height': 'mapData(sizeScore, 0, 100, 28, 88)',
      'background-color': colors.sage[500],
      'border-color': colors.sage[700],
      'border-width': 1.5,
      'opacity': 'mapData(prob, 0, 0.05, 0.5, 1)',
      'label': 'data(displayLabel)',
      'color': colors.ink[900],
      'font-size': 10,
      'text-valign': 'bottom',
      'text-margin-y': 4,
      'transition-property': 'opacity, width, height',
      'transition-duration': 400,
      'transition-timing-function': 'ease-out',
    },
  },
];
